import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AgentDetailView from './AgentDetailView';
import ExternalAgentView from './ExternalAgentView';
import { isExternal } from '../../utils/agentReadiness';
import Btn from '../../components/ui/Btn';
import { agentsService, friendlyError } from '../../services/agentsService';
import { engineService, engineConfigured } from '../../services/engineService';
import { toastSuccess } from '../../utils/toast';

export default function AgentDetailPage() {
  const { agentId } = useParams();
  const id = Number(agentId);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [models, setModels] = useState([]);
  const [state, setState] = useState('loading');   // loading | ready | missing | error
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [d, m] = await Promise.all([agentsService.getAgent(id), agentsService.listModels()]);
      setModels(m);
      if (!d) { setState('missing'); return; }
      setData(d); setState('ready');
    } catch (e) { setError(friendlyError(e)); setState('error'); }
  }, [id]);
  useEffect(() => { setState('loading'); load(); }, [load]);

  // Todo agente de la plataforma tiene su chat web. Si le falta (creado antes de esto o sin motor al crearlo), se crea aquí, una vez.
  const ensured = useRef(null);
  useEffect(() => {
    if (!data || !engineConfigured || isExternal(data.agent) || ensured.current === data.agent.id) return;
    if (data.connections.some((c) => c.channel === 'web')) return;
    ensured.current = data.agent.id;
    engineService.connectWeb(data.agent.id).then(load).catch(() => {});   // si falla, la tarjeta lo dice y se reintenta al volver
  }, [data, load]);

  // Cada acción traduce el error y recarga; el mensaje de éxito usa el mismo verbo que el botón.
  const act = useCallback((fn, ok) => async (...args) => {
    let out;
    try { out = await fn(...args); } catch (e) { throw new Error(friendlyError(e)); }
    if (ok) toastSuccess(ok);
    await load();
    return out;
  }, [load]);

  const actions = useMemo(() => !data ? {} : ({
    // Guardado automático: sin aviso emergente (el panel muestra «Guardado»)
    savePrompt: act((prompt) => agentsService.saveDraft(data.draft.id, { prompt }), null),
    saveBehavior: act(async ({ agentPatch, config }) => {
      await agentsService.updateAgent(data.agent.id, agentPatch);
      await agentsService.saveDraft(data.draft.id, { config });
    }, 'Comportamiento guardado'),
    replaceCredential: act((input) => agentsService.replaceCredential(data.agent.id, input, data.credential), 'Clave guardada'),
    updateCredential: act((id, patch) => agentsService.updateCredential(id, patch), 'Clave actualizada'),
    useCredential: act((credId) => agentsService.useCredential(data.agent.id, credId), 'Clave asignada'),
    rename: act((name) => agentsService.renameAgent(data.agent.id, name), 'Nombre cambiado'),
    publish: act(() => agentsService.publish(data.agent.id), 'Publicado'),
    archive: act(() => agentsService.archive(data.agent.id), 'Agente archivado'),
    restore: act(() => agentsService.restore(data.agent.id), 'Agente restaurado'),
    setEnvironment: (env) => act(() => agentsService.setEnvironment(data.agent.id, env), env === 'production' ? 'Agente en producción' : env === 'demo' ? 'Agente en demo' : 'Agente en pruebas')(),
    setVisibility: (v) => act(() => agentsService.setVisibility(data.agent.id, v), v === 'public' ? 'Agente público: el equipo lo puede clonar' : 'Agente privado: solo tú lo puedes clonar')(),
    setEngine: (engine) => act(() => agentsService.setEngine(data.project.id, engine), engine === 'runtime' ? 'Motor encendido' : 'Motor apagado')(),
    connectTelegram: act((token) => engineService.connectTelegram(data.agent.id, token), 'Telegram conectado'),
    connectWhatsapp: act((signup) => engineService.connectWhatsapp(data.agent.id, signup), 'WhatsApp conectado'),
    setWebOrigins: act((origins) => engineService.connectWeb(data.agent.id, origins), 'Sitios guardados'),
    saveWebLook: act(async (look) => {
      const r = await engineService.connectWeb(data.agent.id, undefined, look);
      if (!r || typeof r.look !== 'object') throw new Error('El motor todavía no tiene esta versión y no guardó la apariencia. Despliégalo (./deploy.sh) y vuelve a guardar.');
    }, 'Apariencia aplicada al chat'),
    disconnect: act((connId) => engineService.disconnect(connId), 'Canal desconectado'),
    // Abre el mismo formulario de edición de Proyectos (permisos, clientes, módulos…)
    openProject: async () => {
      try { const p = await agentsService.getProjectForEdit(data.project.id); navigate('/proyects/edit', { state: { isEdit: true, proyectEdit: p } }); }
      catch (e) { throw new Error(friendlyError(e)); }
    },
    setManagedBy: act((input) => agentsService.setManagedBy(data.agent.id, input), 'Guardado'),
    // Herramientas del agente: van en la configuración del borrador (se usan al publicar)
    saveTools: act((customTools) => agentsService.saveDraft(data.draft.id, { config: { ...(data.draft.config || {}), custom_tools: customTools } }), 'Herramientas guardadas en el borrador'),
    savePreTools: act((preTools) => agentsService.saveDraft(data.draft.id, { config: { ...(data.draft.config || {}), pre_tools: preTools } }), 'Datos previos guardados en el borrador'),
    copyToDraft: act((v) => agentsService.saveDraft(data.draft.id, { prompt: v.prompt, config: v.config }), 'Copiado al borrador'),
  }), [data, act, navigate]);

  if (state === 'loading') return <div className="max-w-6xl mx-auto" aria-busy="true"><div className="h-8 w-64 skeleton" /><div className="mt-8 h-24 skeleton opacity-50" /></div>;
  if (state === 'missing') return (
    <div className="max-w-6xl mx-auto text-sm text-fg-dim">
      <p className="text-fg font-medium">Ese agente no existe o ya no tienes acceso.</p>
      <Link to="/agentes" className="mt-3 inline-block text-live hover:underline">Volver a Agentes</Link>
    </div>
  );
  if (state === 'error') return (
    <div role="alert" className="max-w-6xl mx-auto rounded-xl border border-signal/30 bg-signal-soft p-5 text-sm">
      <p className="text-fg font-medium">No se pudo cargar el agente.</p><p className="mt-1 text-fg-dim">{error}</p>
      <Btn size="sm" className="mt-3" onClick={() => { setState('loading'); load(); }}>Reintentar</Btn>
    </div>
  );
  if (isExternal(data.agent)) return <ExternalAgentView data={data} actions={actions} />;
  return <AgentDetailView data={data} models={models} engineReady={engineConfigured} actions={actions} />;
}
